// 전직 상태와 전직 명령(3개월 C10-5, 데이터·판정은 shared/content/jobQuest.ts, 정본 docs/SKILL-TREE.md 13절).
// 관문 판 자체는 용사의 시련 판 흐름(game/trialRun.ts)이 돌린다(같은 조건 판·문턱 실패·고정 id 지급). 이 모듈은
//  - 저장소 영수증(save.v1 고정 id)에서 전직 단계·갈래·통과한 관문을 다시 읽고(refresh),
//  - 지금 영웅의 성장 장부에 전직 스킬 비트(카엘 9·10, 라이라 12·13)를 붙이고(GrowthStore.setJobBits — 장부에 쓰지 않는 파생 값),
//  - 교관 앞 명령(갈래 선택 choose·승급 advance)을 받아 영수증을 적고 Ev.JobChange를 낸다(연출은 UI가 이 이벤트로 튼다).
// 새 저장 키가 없다: 동기화 세대(SYNC_GEN)를 올리지 않는다. 고정 스텝에서는 부르지 않는다(마을 진입·지급 뒤·명령 때만).
import { Ev } from '../shared/events';
import { JOB_GATES, JOB_LEVEL, isJobHero, jobAdvanceId, jobGateOpen, jobGateRewardId, jobPickId, jobSkillMask, jobState, jobTierPassed, type JobBranch, type JobGateDef, type JobState, type JobTier, type JobView } from '../shared/content/jobQuest';
import { Scene } from '../shared/types';
import type { GameSim } from './game';

/** Ev.JobChange d: 거절 이유 */
export const JOB_DENY = { hero: 1, scene: 2, level: 3, gates: 4, done: 5, order: 6, branch: 7 } as const;

export class JobRun {
  private st: JobState = { tier: 0, branch: 0 };
  private readonly passedList: string[] = [];
  private readonly openList: string[] = [];
  private readyTier = 0;
  private revNo = 0;
  /** 마지막 refresh의 영웅(영웅을 바꾸면 목록이 같아 보여도 rev를 올린다) */
  private lastHero = '';
  readonly view: JobView;

  constructor(private readonly g: GameSim) {
    const self = this;
    this.view = {
      get available() { return isJobHero(self.g.heroId); }, get hero() { const h = self.g.heroId; return isJobHero(h) ? h : null; }, get tier() { return self.st.tier; }, get branch() { return self.st.branch; },
      get passed() { return self.passedList; }, get open() { return self.openList; }, get ready() { return self.readyTier; }, get rev() { return self.revNo; },
    };
  }

  /** 지금 영웅의 전직 갈래(전직 스킬 타임라인 고르기: 카엘 9·10, 라이라 12·13). 전직 전·다른 영웅은 0 */
  get branch(): JobBranch { return isJobHero(this.g.heroId) ? this.st.branch : 0; }
  get tier(): number { return isJobHero(this.g.heroId) ? this.st.tier : 0; }
  /** 지금 영웅의 전직 스킬 비트(성장 장부 kept에 붙는다) */
  get skillBits(): number { const h = this.g.heroId; return isJobHero(h) ? jobSkillMask(h, this.st) : 0; }

  passed(id: string): boolean { return this.g.save.hasResult(jobGateRewardId(id)); }
  gateOpen(gate: JobGateDef): boolean {
    return isJobHero(this.g.heroId) && gate.hero === this.g.heroId && jobGateOpen(gate, this.g.growth.level, this.st, (id) => this.passed(id));
  }

  /** 저장소 기준으로 상태를 다시 만들고 성장 장부에 전직 스킬 비트를 붙인다. 고정 스텝 밖에서만 부른다 */
  refresh(): void {
    const g = this.g, hero = g.heroId;
    const st: JobState = isJobHero(hero) ? jobState(hero, (id) => g.save.hasResult(id)) : { tier: 0, branch: 0 };
    const p: string[] = [], o: string[] = [];
    let ready = 0;
    if (isJobHero(hero)) {
      for (const gate of JOB_GATES) {
        if (gate.hero !== hero) continue;
        if (this.passed(gate.id)) p.push(gate.id);
        if (jobGateOpen(gate, g.growth.level, st, (id) => this.passed(id))) o.push(gate.id);
      }
      const has = (id: string) => this.passed(id);
      if (st.tier === 0 && jobTierPassed(hero, 1, has)) ready = 1;
      else if (st.tier === 1 && jobTierPassed(hero, 2, has)) ready = 2;
    }
    g.growth.setJobBits(isJobHero(hero) ? jobSkillMask(hero, st) : 0);
    const same = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x, i) => x === b[i]);
    if (hero === this.lastHero && st.tier === this.st.tier && st.branch === this.st.branch && ready === this.readyTier && same(p, this.passedList) && same(o, this.openList)) return;
    this.lastHero = hero; this.st = st; this.readyTier = ready;
    this.passedList.length = 0; this.passedList.push(...p);
    this.openList.length = 0; this.openList.push(...o);
    this.revNo++;
  }

  private emit(tier: number, branch: number, ok: number, code: number): void {
    const e = this.g.emit(Ev.JobChange); e.a = tier; e.b = branch; e.c = ok; e.d = code;
  }

  /** 공통 조건: 전직 영웅·마을(이동·일시정지 아님) */
  private denyCommon(tier: JobTier, branch: number): boolean {
    const g = this.g, f = g.flow;
    if (!isJobHero(g.heroId)) { this.emit(tier, branch, 0, JOB_DENY.hero); return true; }
    if (f.scene !== Scene.Village || f.paused || f.travelTo !== 0) { this.emit(tier, branch, 0, JOB_DENY.scene); return true; }
    if (g.growth.level < JOB_LEVEL[tier]) { this.emit(tier, branch, 0, JOB_DENY.level); return true; }
    return false;
  }

  /** 전직 I 갈래 선택(교관 앞). 관문 3개 통과·Lv30·아직 전직 전일 때만. 되돌릴 수 없다 */
  choose(branch: unknown): boolean {
    const b = branch === 0 || branch === 1 ? branch : -1;
    if (b < 0) { this.emit(1, -1, 0, JOB_DENY.branch); return false; }
    if (this.denyCommon(1, b)) return false;
    const g = this.g, hero = g.heroId;
    if (!isJobHero(hero)) return false;
    // 다른 탭이 먼저 고른 갈래가 저장소에 있으면 그것을 보고 거절한다(덮어쓰기 방지, PR #334 리뷰 P2)
    g.save.refresh();
    this.refresh();
    if (this.st.tier >= 1) { this.emit(1, b, 0, JOB_DENY.done); return false; }
    if (!jobTierPassed(hero, 1, (id) => this.passed(id))) { this.emit(1, b, 0, JOB_DENY.gates); return false; }
    this.record(jobPickId(hero, b as JobBranch), 2);
    this.refresh();
    this.afterChange();
    this.emit(1, this.st.branch, 1, 0);
    return true;
  }

  /** 전직 II 승급(교관 앞). 전직 I 완료·관문 3개 통과·Lv40일 때만 */
  advance(): boolean {
    if (this.denyCommon(2, this.st.branch)) return false;
    const g = this.g, hero = g.heroId;
    if (!isJobHero(hero)) return false;
    g.save.refresh();
    this.refresh();
    if (this.st.tier >= 2) { this.emit(2, this.st.branch, 0, JOB_DENY.done); return false; }
    if (this.st.tier < 1) { this.emit(2, this.st.branch, 0, JOB_DENY.order); return false; }
    if (!jobTierPassed(hero, 2, (id) => this.passed(id))) { this.emit(2, this.st.branch, 0, JOB_DENY.gates); return false; }
    this.record(jobAdvanceId(hero), 3);
    this.refresh();
    this.afterChange();
    this.emit(2, this.st.branch, 1, 0);
    return true;
  }

  /** 결정 영수증(골드 0, save.v1 고정 id 보너스 영수증 — 몇 번을 불러도 한 번) */
  private record(id: string, region: 2 | 3): void {
    this.g.save.recordResult({ id, region: region === 2 ? 'r2' : 'r3', baseGold: 0, clearTicks: 0, maxCombo: 0, stars: 0, starMask: 0, kills: 0, damageDealt: 0, cards: [0, 0, 0], pickedCard: 0, bonus: true });
  }

  /** 스킬 표·능력치·슬롯을 새 전직 상태로 다시 맞춘다 */
  private afterChange(): void {
    this.g.syncGrowth();
    this.g.profile.sync();
  }
}
